import useSubdomain from 'hooks/useSubdomain/useSubdomain'
import { useCallback, useEffect, useState } from 'react'
import getEnv from 'utilities/getEnv'
import { setTenantToHttpRequest } from 'store/actions/utils'
import globalService from 'Services/api/global.service'
import { EnumRegisterStatus } from 'interfaces'
import RegisterPage from 'pages/RegisterPage'
import SplashPage from 'pages/SplashPage'
import InstallingScreen from './InstallingScreen/InstallingScreen'
import ErrorPage from 'pages/ErrorPage/ErrorPage'
import InstallingSplashScreen from './InstallingScreen/InstallingSplashScreen/InstallingSplashScreen'
import androidWebViewApi from 'utilities/androidWebViewApi'

/**
 * Start --> Installing
 * Installing --> Installed | Error
 * Installed --> HasTenant | Error
 */
enum InitAppState {
    /** Initial state. */
    Start = 'Start',

    /** State while checking installation. Right now, it's state when user's waiting after they registered new companny */
    Installing = 'Installing',

    /** State when check installation is complete. */
    Installed = 'Installed',

    /** State when tenent info has successfully loaded */
    HasTenant = 'HasTenant',

    /** State when has error while checking installation or loading tenant information */
    Error = 'Error',
}

export function AppInstallProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const { hasSubDomain, subdomain } = useSubdomain()

    const [initAppState, setInitAppState] = useState<InitAppState>(
        InitAppState.Start,
    )
    const [error, setError] = useState<string | null>(null)

    const checkLocationInstallStatus = useCallback(async () => {
        if (hasSubDomain) {
            try {
                const COMPANY_TENANT_IDENTIFIER_ID = getEnv(
                    'REACT_APP_COMPANY_TENANT_IDENTIFIER_ID',
                )
                const LOCATION_TENANT_IDENTIFIER_ID = getEnv(
                    'REACT_APP_LOCATION_TENANT_IDENTIFIER_ID',
                )

                if (
                    !COMPANY_TENANT_IDENTIFIER_ID ||
                    !LOCATION_TENANT_IDENTIFIER_ID
                ) {
                    setError(
                        'COMPANY_TENANT_IDENTIFIER_ID or LOCATION_TENANT_IDENTIFIER_ID is not defined',
                    )
                    setInitAppState(InitAppState.Error)
                    return
                }

                const tenant = {
                    companyTenantIdentifierID: COMPANY_TENANT_IDENTIFIER_ID,
                    locationTenantIdentifierID: LOCATION_TENANT_IDENTIFIER_ID,
                }

                setTenantToHttpRequest(tenant)

                const registerStatus = await globalService.checkRegisterStatus(
                    subdomain,
                )
                if (registerStatus.data === EnumRegisterStatus.InProgress)
                    setInitAppState(InitAppState.Installing)
                else setInitAppState(InitAppState.Installed)
            } catch (err: any) {
                console.log('checkLocationInstallStatus: ', err)
                setError(err?.message)
                setInitAppState(InitAppState.Error)
            }
        }
    }, [hasSubDomain, subdomain])

    const [displayInstalling, setDisplayInstalling] = useState<boolean>(true)

    const handleFinishInstall = useCallback(() => {
        setInitAppState(InitAppState.Installed)
    }, [])

    useEffect(() => {
        checkLocationInstallStatus()
    }, [checkLocationInstallStatus])

    useEffect(() => {
        const timer = setTimeout(() => {
            setDisplayInstalling(false)
        }, 2000)
        return () => {
            clearTimeout(timer)
        }
    }, [])

    // Trigger onFinishLoaded event on Android WebView
    useEffect(() => {
        androidWebViewApi.onFinishLoaded()
    }, [])

    if (displayInstalling) return <InstallingSplashScreen />
    if (hasSubDomain) {
        if (initAppState === InitAppState.Start) return <SplashPage />
        else if (initAppState === InitAppState.Installing)
            return <InstallingScreen onFinishInstall={handleFinishInstall} />
        else if (initAppState === InitAppState.Error)
            return <ErrorPage message={error ?? 'error while initialize app'} />
        else return <>{children}</>
    } else return <RegisterPage />
}
